import { CDN_URL } from "@/utils/config";
import { getDarkColor, getLightColor } from "../../../utils/src/color";
import AIAvatar from "../assets/images/ai-avatar.png";

export type ThemeOptions = {
    /**
     * 系统名称
     */
    systemName: string;
    /**
     * 系统简称
     */
    systemAbbreviation: string;
    /**
     * 系统头部标语
     */
    systemHeaderSlogan: string;
    /**
     * AI名称
     */
    aiName: string;
    /**
     * 服务电话
     */
    serviceTel: string;
    /**
     * 版权
     */
    copyright: string;
    /**
     * 备案
     */
    icpFilings: string;
    /**
     * 联网备案信息
     */
    internetFilings: string;
    /**
     * 主题颜色
     */
    themeColor: string;
    /**
     * 登录页logo
     */
    loginLogo: string;
    /**
     * 登录页背景图
     */
    loginBgColor: string;
    /**
     * 公众页头部logo
     */
    publicHeaderLogo: string;
    /**
     * 公众页横幅
     */
    publicBanner: string;
    /**
     * AI形象
     */
    AIImage: string;
    /**
     * 公众页底部logo
     */
    publicFooterLogo: string;
    /**
     * 控制台头部logo
     */
    consoleLogo: string;
    /**
     * 账号头像
     */
    accountAvatar: string;
    /**
     * AI头像
     */
    AIAvatar: string;
    buttonTextColor: string;
};

export type ITheme = {
    /**
     * 主题色列表
     */
    themeColors: {
        light: string[];
        dark: string[];
    };
    /**
     * 按钮文本颜色
     * */
    buttonTextColor: string;
} & ThemeOptions;

export const defaultTheme: ThemeOptions = {
    systemName: "链云砂石",
    systemAbbreviation: "链云",
    systemHeaderSlogan: "产业新质生产力服务商",
    aiName: "链小云",
    serviceTel: "400-0707-798",
    copyright: `Copyright © ${new Date().getFullYear()} 志豪链云科技有限公司 All Rights Reserved.`,
    icpFilings: "鄂ICP备2024034058号-1",
    internetFilings: "",
    themeColor: "#FFDB5C",
    loginLogo: `${CDN_URL}/logo/shaheshang-logo.png`,
    loginBgColor: `${CDN_URL}/customer-admin/login-bg.jpg`,
    publicHeaderLogo: `${CDN_URL}/logo/shaheshang-logo.png`,
    publicBanner: "",
    AIImage: `${CDN_URL}/customer-admin/ai.png`,
    publicFooterLogo: `${CDN_URL}/logo/shaheshang-logo.png`,
    consoleLogo: `${CDN_URL}/logo/shaheshang-logo.png`,
    accountAvatar: `${CDN_URL}/customer-admin/avatar.svg`,
    AIAvatar: AIAvatar,
    buttonTextColor: "#333"
};

/**
 * 主题
 */
export default class Theme implements ITheme {
    /**
     * 系统名称
     */
    private _systemName: string = "";
    /**
     * 系统简称
     */
    private _systemAbbreviation: string = "";
    /**
     * 系统头部标语
     */
    private _systemHeaderSlogan: string = "";
    /**
     * AI名称
     */
    private _aiName: string = "";
    /**
     * 服务电话
     */
    private _serviceTel: string = "";
    /**
     * 版权
     */
    private _copyright: string = "";

    /**
     * 备案
     */
    private _icpFilings: string = "";
    /**
     * 联网备案信息
     */
    private _internetFilings: string = "";

    /**
     * 主题颜色
     */
    private _themeColor: string = "";
    /**
     * 登录页logo
     */
    private _loginLogo: string = "";
    /**
     * 登录页背景图
     */
    private _loginBgColor: string = "";
    /**
     * 公众页头部logo
     */
    private _publicHeaderLogo: string = "";
    /**
     * 公众页横幅
     */
    private _publicBanner: string = "";
    /**
     * AI形象
     */
    private _AIImage: string = "";
    /**
     * 公众页底部logo
     */
    private _publicFooterLogo: string = "";
    /**
     * 控制台头部logo
     */
    private _consoleLogo: string = "";
    /**
     * 账号头像
     */
    private _accountAvatar: string = "";
    /**
     * AI头像
     */
    private _AIAvatar: string = "";

    /**
     * 主题色列表
     */
    private _themeColors: ITheme["themeColors"] = { light: [], dark: [] };
    /**
     * 按钮文本颜色
     * */
    private _buttonTextColor: string = "#ffffff";

    constructor() {}

    /**
     * 设置主题
     */
    setTheme(theme: ThemeOptions) {
        this._systemName = theme.systemName;
        this._systemAbbreviation = theme.systemAbbreviation;
        this._systemHeaderSlogan = theme.systemHeaderSlogan;
        this._aiName = defaultTheme.aiName;
        this._serviceTel = theme.serviceTel;
        this._copyright = theme.copyright;
        this._themeColor = theme.themeColor;
        this._loginLogo = theme.loginLogo;
        this._loginBgColor = theme.loginBgColor;
        this._publicHeaderLogo = theme.publicHeaderLogo;
        this._publicBanner = theme.publicBanner;
        this._AIImage = theme.AIImage;
        this._publicFooterLogo = theme.publicFooterLogo;
        this._consoleLogo = theme.consoleLogo;
        this._accountAvatar = theme.accountAvatar;
        this._AIAvatar = theme.AIAvatar;
        this._icpFilings = theme.icpFilings;
        this._internetFilings = theme.internetFilings;
        this.setThemeColors(theme.themeColor);
        this.setButtonTextColor(theme.themeColor);
    }

    /**
     * 根据主题颜色设置css变量
     */
    private setThemeColors(themeColor: string) {
        /**setTime保证document可以拿到*/
        setTimeout(() => {
            window?.document?.documentElement.style.setProperty("--el-color-theme", themeColor);

            const begin = 5;
            const lightLevelStep = 1 / begin;
            for (let i = begin; i >= 1; i--) {
                const lightLevel = i === begin ? 0.95 : lightLevelStep * i;
                const lightColor = `${getLightColor(themeColor, lightLevel)}`;
                window?.document?.documentElement.style.setProperty(`--el-color-theme-light-${6 - i}`, lightColor);
                this._themeColors.light[i] = lightColor;
            }

            for (let i = 0; i <= 3; i++) {
                const darkColor = `${getDarkColor(themeColor, i / 10)}`;
                window?.document?.documentElement.style.setProperty(`--el-color-theme-dark-${i + 6}`, darkColor);
                this._themeColors.dark[i] = darkColor;
            }
        });
    }

    /**
     *  根据主题色计算按钮文本的颜色
     * */
    private setButtonTextColor(themeColor: string) {
        /**setTime保证document可以拿到*/
        setTimeout(() => {
            let buttonTextColor = "#ffffff";
            // 把主题色16进制改成10进制
            const R = parseInt(themeColor.slice(1, 3), 16);
            const G = parseInt(themeColor.slice(3, 5), 16);
            const B = parseInt(themeColor.slice(5, 7), 16);
            // 根据公式计算亮度值
            const brightnessValue = 0.2126 * R + 0.7152 * G + 0.0722 * B;
            if (brightnessValue > 128) buttonTextColor = "#333333";
            window?.document?.documentElement.style.setProperty("--el-color-button-text-color", buttonTextColor);
            setTimeout(() => {
                this._buttonTextColor = buttonTextColor;
            });
        });
    }

    get systemName() {
        return this._systemName;
    }

    get systemAbbreviation() {
        return this._systemAbbreviation;
    }

    get systemHeaderSlogan() {
        return this._systemHeaderSlogan;
    }

    get aiName() {
        return this._aiName;
    }

    get serviceTel() {
        return this._serviceTel;
    }

    get copyright() {
        return this._copyright;
    }

    get themeColor() {
        return this._themeColor;
    }

    get loginLogo() {
        return this._loginLogo;
    }

    get loginBgColor() {
        return this._loginBgColor;
    }

    get publicHeaderLogo() {
        return this._publicHeaderLogo;
    }

    get publicBanner() {
        return this._publicBanner;
    }

    get AIImage() {
        return this._AIImage;
    }

    get publicFooterLogo() {
        return this._publicFooterLogo;
    }

    get consoleLogo() {
        return this._consoleLogo;
    }

    get accountAvatar() {
        return this._accountAvatar;
    }

    get AIAvatar() {
        return this._AIAvatar;
    }

    get themeColors() {
        return this._themeColors;
    }

    get buttonTextColor() {
        return this._buttonTextColor;
    }

    get icpFilings() {
        return this._icpFilings;
    }

    get internetFilings() {
        return this._internetFilings;
    }

    /**
     * 单例
     * @private
     */
    private static instance: Theme;

    /**
     * 获取单例
     */
    static getInstance() {
        if (!Theme.instance) {
            Theme.instance = new Theme();
        }
        return Theme.instance;
    }
}

export const theme = Theme.getInstance();
